<template>
  <div class="nutrition-progress">
    <!-- Overall Progress Circle -->
    <div v-if="showOverall" class="overall-progress">
      <div class="progress-circle" :style="circleStyle">
        <div class="circle-inner">
          <span class="circle-value">{{ completion.overall }}%</span>
          <span class="circle-label">Complete</span>
        </div>
      </div>
    </div>

    <!-- Macro Progress Bars -->
    <div class="macro-bars">
      <!-- Carbs -->
      <div class="macro-bar">
        <div class="macro-header">
          <span class="macro-icon carbs-icon">C</span>
          <span class="macro-label">Carbs</span>
          <span class="macro-values">
            {{ formatValue(totals.total_carbs) }} / {{ formatValue(targets.carbs) }}g
          </span>
        </div>
        <div class="progress-track">
          <div
            class="progress-fill carbs-fill"
            :style="{ width: completion.carbs + '%' }"
            :class="{ over: completion.carbs > 100 }"
          ></div>
        </div>
        <span class="macro-percent">{{ completion.carbs }}%</span>
      </div>

      <!-- Protein -->
      <div class="macro-bar">
        <div class="macro-header">
          <span class="macro-icon protein-icon">P</span>
          <span class="macro-label">Protein</span>
          <span class="macro-values">
            {{ formatValue(totals.total_protein) }} / {{ formatValue(targets.protein) }}g
          </span>
        </div>
        <div class="progress-track">
          <div
            class="progress-fill protein-fill"
            :style="{ width: completion.protein + '%' }"
            :class="{ over: completion.protein > 100 }"
          ></div>
        </div>
        <span class="macro-percent">{{ completion.protein }}%</span>
      </div>

      <!-- Fat -->
      <div class="macro-bar">
        <div class="macro-header">
          <span class="macro-icon fat-icon">F</span>
          <span class="macro-label">Fat</span>
          <span class="macro-values">
            {{ formatValue(totals.total_fat) }} / {{ formatValue(targets.fat) }}g
          </span>
        </div>
        <div class="progress-track">
          <div
            class="progress-fill fat-fill"
            :style="{ width: completion.fat + '%' }"
            :class="{ over: completion.fat > 100 }"
          ></div>
        </div>
        <span class="macro-percent">{{ completion.fat }}%</span>
      </div>
    </div>

    <!-- Calories Summary (optional) -->
    <div v-if="showCalories" class="calories-summary">
      <div class="calories-label">
        {{ energyFraming === 'floor' ? 'Aim to eat at least' : 'Total Calories' }}
      </div>
      <div class="calories-value">
        {{ formatValue(totals.total_calories) }}
        <span class="calories-target">
          {{ energyFraming === 'floor' ? 'of at least' : '/' }}
          {{ formatValue(targets.calories) }} kcal
        </span>
      </div>
      <p v-if="energyFraming === 'floor'" class="calories-floor-note">
        A floor to fuel your day — more on hard days, never less.
      </p>
    </div>

    <!-- Context Badge (optional) -->
    <div v-if="context && showContext" class="context-badge" :style="contextStyle">
      {{ contextInfo.label }}
    </div>
  </div>
</template>

<script>
import { Nutrition } from "@/services/nutrition-service";

export default {
  name: "NutritionProgress",

  props: {
    totals: {
      type: Object,
      default: () => ({
        total_carbs: 0,
        total_protein: 0,
        total_fat: 0,
        total_hydration: 0,
        total_calories: 0,
      }),
    },
    targets: {
      type: Object,
      default: () => ({
        carbs: 0,
        protein: 0,
        fat: 0,
        hydration: 0,
        calories: 0,
      }),
    },
    completion: {
      type: Object,
      default: () => ({
        overall: 0,
        carbs: 0,
        protein: 0,
        fat: 0,
        hydration: 0,
      }),
    },
    context: {
      type: String,
      default: null,
    },
    showOverall: {
      type: Boolean,
      default: true,
    },
    showCalories: {
      type: Boolean,
      default: true,
    },
    // Welfare framing for any energy figure shown to a player. 'floor' (default)
    // presents the number as a minimum to reach ("eat at least"); 'neutral' is
    // for the staff monitoring view. See useNutritionAccess.js.
    energyFraming: {
      type: String,
      default: "floor",
    },
    showContext: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    circleStyle() {
      const percent = Math.min(100, this.completion.overall);
      return {
        background: `conic-gradient(#28a745 ${percent}%, #e9ecef ${percent}%)`,
      };
    },

    contextInfo() {
      return Nutrition.getContextInfo(this.context);
    },

    contextStyle() {
      const info = this.contextInfo;
      return {
        backgroundColor: info.bgColor,
        color: info.color,
        borderColor: info.color,
      };
    },
  },

  methods: {
    formatValue(value) {
      if (value === null || value === undefined) return "0";
      return Math.round(value);
    },

  },
};
</script>

<style scoped lang="scss">
.nutrition-progress {
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.overall-progress {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.progress-circle {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.circle-inner {
  width: 80px;
  height: 80px;
  background: white;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.circle-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
}

.circle-label {
  font-size: 0.7rem;
  color: #666;
  text-transform: uppercase;
}

.macro-bars {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.macro-bar {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: center;
}

.macro-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.macro-icon {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: white;

  &.carbs-icon {
    background: linear-gradient(135deg, #ffc107, #ff9800);
  }

  &.protein-icon {
    background: linear-gradient(135deg, #dc3545, #c82333);
  }

  &.fat-icon {
    background: linear-gradient(135deg, #28a745, #20c997);
  }

}

.macro-label {
  font-weight: 600;
  color: #333;
  min-width: 60px;
}

.macro-values {
  color: #666;
  font-size: 0.85rem;
  margin-left: auto;
}

.progress-track {
  grid-column: 1 / 2;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;

  &.carbs-fill {
    background: linear-gradient(90deg, #ffc107, #ff9800);
  }

  &.protein-fill {
    background: linear-gradient(90deg, #dc3545, #c82333);
  }

  &.fat-fill {
    background: linear-gradient(90deg, #28a745, #20c997);
  }

  &.over {
    background: linear-gradient(90deg, #6f42c1, #e83e8c);
  }
}

.macro-percent {
  font-size: 0.8rem;
  font-weight: 600;
  color: #666;
  min-width: 40px;
  text-align: right;
}

.calories-summary {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.calories-label {
  font-weight: 600;
  color: #333;
}

.calories-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;

  .calories-target {
    font-weight: 400;
    color: #666;
    font-size: 0.9rem;
  }
}

.calories-floor-note {
  margin: 6px 0 0;
  font-size: 0.78rem;
  font-style: italic;
  color: #6b7280;
}

.context-badge {
  margin-top: 12px;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  border: 1px solid;
}

/* Compact Mode */
.nutrition-progress.compact {
  .overall-progress {
    display: none;
  }

  .macro-bars {
    gap: 10px;
  }

  .macro-header {
    font-size: 0.8rem;
  }

  .progress-track {
    height: 6px;
  }

  .calories-summary {
    margin-top: 10px;
    padding-top: 10px;
  }
}

/* Mobile adjustments */
@media screen and (max-width: 480px) {
  .nutrition-progress {
    padding: 12px;
  }

  .progress-circle {
    width: 80px;
    height: 80px;
  }

  .circle-inner {
    width: 64px;
    height: 64px;
  }

  .circle-value {
    font-size: 1.2rem;
  }

  .macro-bars {
    gap: 12px;
  }

  .macro-header {
    font-size: 0.85rem;
  }

  .macro-label {
    min-width: 50px;
  }
}
</style>
